import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Timer, ArrowUp, ArrowDown } from "lucide-react"
import type { GameQuestion, PlayerPowerUp } from '@/types/games'

interface SequenceGameProps {
  gameId: string
  question: GameQuestion
  powerUps?: PlayerPowerUp[]
  onComplete: (score: number, time: number) => void
  onUsePowerUp?: (powerUpId: string) => Promise<boolean>
}

export function SequenceGame({
  question,
  onComplete,
  gameId: _gameId,
  powerUps: _powerUps,
  onUsePowerUp: _onUsePowerUp
}: SequenceGameProps) {
  const [sequence, setSequence] = useState<number[]>(
    [...Array(question.answers.length)].map((_, i) => i)
  )
  const [timeLeft, setTimeLeft] = useState(60)
  const [score, setScore] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 0) {
          clearInterval(timer)
          checkAnswer()
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [])

  const moveItem = (from: number, to: number) => {
    const newSequence = [...sequence]
    const [removed] = newSequence.splice(from, 1)
    newSequence.splice(to, 0, removed)
    setSequence(newSequence)
  }

  const checkAnswer = () => {
    const correctOrder = (question as any).sequence_order || []
    const isCorrect = sequence.every((item, index) => correctOrder[index] === item + 1)
    
    const timeBonus = Math.max(0, timeLeft * 10)
    const finalScore = isCorrect ? 1000 + timeBonus : 0
    
    setScore(finalScore)
    onComplete(finalScore, 60 - timeLeft)
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Sequence Challenge</span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Timer className="h-4 w-4" />
              <span className={timeLeft <= 10 ? "text-destructive" : ""}>
                {timeLeft}s
              </span>
            </div>
            <span className="text-lg font-normal">
              Score: {score}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-lg font-medium">
          {question.question}
        </div>

        <div className="space-y-2">
          {sequence.map((index, position) => (
            <div
              key={index}
              className="flex items-center gap-2 p-4 bg-muted rounded-lg"
            >
              <div className="flex flex-col gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={position === 0}
                  onClick={() => moveItem(position, position - 1)}
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={position === sequence.length - 1}
                  onClick={() => moveItem(position, position + 1)}
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex-1">
                {typeof question.answers[index] === 'string' 
                  ? question.answers[index] 
                  : (question.answers[index] as any).text}
              </div>
            </div>
          ))}
        </div>

        <Button
          className="w-full"
          onClick={checkAnswer}
        >
          Submit Order
        </Button>
      </CardContent>
    </Card>
  )
}